// Tab "Ảnh liệu trình": chụp / tải ảnh theo thẻ – buổi – giai đoạn, xem theo tiến trình, so sánh trước / sau (cạnh nhau hoặc thanh trượt)
import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, DatePicker, Input, Modal, Segmented, Select, Spin, message } from 'antd';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, Columns2, EyeOff, ImagePlus, ShieldAlert, SlidersHorizontal, Trash2, Upload } from 'lucide-react';
import { useTreatmentAuth } from '../Treatment/actions';
import type { TreatmentCard } from '../Treatment/data';
import { fmtDateTime } from '../Treatment/ui';
import { PHASE_CLS, PHASE_LABEL, PHOTO_ANGLES, type PhotoPhase, type TreatmentPhoto } from './data';
import { compressImage, useMedia } from './mediaDb';
import useC360Store from './store';
import { Btn, type C360Ops } from './ui360';
import type { C360 } from './useCustomer360';

/** Số thứ tự buổi (theo thời gian, bỏ buổi đã hủy) */
const sessionNoOf = (card: TreatmentCard | undefined, usageId?: string) => {
  if (!card || !usageId) return undefined;
  const live = card.usages.filter(u => !u.cancelled).sort((a, b) => a.at.localeCompare(b.at));
  const i = live.findIndex(u => u.id === usageId);
  return i >= 0 ? i + 1 : undefined;
};

export default function PhotoPanel({ c, ops, uploadReq, onUploadHandled }: {
  c: C360; ops: C360Ops; uploadReq: { cardId?: string; usageId?: string; n: number } | null; onUploadHandled: () => void;
}) {
  const { can } = useTreatmentAuth();
  const [cardFilter, setCardFilter] = useState<string>('all');
  const [phase, setPhase] = useState<'all' | PhotoPhase>('all');
  const [showHidden, setShowHidden] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [viewing, setViewing] = useState<string | null>(null);
  const [compare, setCompare] = useState<[TreatmentPhoto, TreatmentPhoto] | null>(null);
  const [upload, setUpload] = useState<{ cardId?: string; usageId?: string } | null>(null);

  useEffect(() => {
    if (uploadReq) { setUpload({ cardId: uploadReq.cardId, usageId: uploadReq.usageId }); onUploadHandled(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadReq?.n]);

  const consent = c.data.docs.some(d => d.kind === 'photo' && d.status === 'signed');
  const photos = c.data.photos
    .filter(p => showHidden || !p.hidden)
    .filter(p => cardFilter === 'all' || (cardFilter === 'none' ? !p.cardId : p.cardId === cardFilter))
    .filter(p => phase === 'all' || p.phase === phase)
    .sort((a, b) => a.takenAt.localeCompare(b.takenAt));

  // Nhóm theo thẻ → buổi / ngày chụp
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; card?: TreatmentCard; label: string; at: string; items: TreatmentPhoto[] }>();
    photos.forEach(p => {
      const card = c.data.cards.find(x => x.id === p.cardId);
      const key = `${p.cardId || '-'}|${p.usageId || p.takenAt.slice(0, 10)}`;
      if (!map.has(key)) {
        const sess = p.sessionNo ? `Buổi ${p.sessionNo}` : '';
        map.set(key, { key, card, at: p.takenAt, items: [], label: [card?.packageName || 'Không gắn thẻ', sess, dayjs(p.takenAt).format('DD/MM/YYYY')].filter(Boolean).join(' · ') });
      }
      map.get(key)!.items.push(p);
    });
    return Array.from(map.values()).sort((a, b) => b.at.localeCompare(a.at));
  }, [photos, c.data.cards]);

  const quickCompare = (cardId: string) => {
    const list = c.data.photos.filter(p => p.cardId === cardId && !p.hidden).sort((a, b) => a.takenAt.localeCompare(b.takenAt));
    const before = list.find(p => p.phase === 'before') || list[0];
    const after = [...list].reverse().find(p => p.phase === 'after' && p.angle === before?.angle) || [...list].reverse().find(p => p.id !== before?.id);
    if (before && after && before.id !== after.id) setCompare([before, after]);
    else message.info('Cần ít nhất 2 ảnh của thẻ này để so sánh');
  };

  const togglePick = (id: string) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p.slice(-1), id]));
  const flat = photos;
  const cardsWithPhotos = c.data.cards.filter(x => c.data.photos.some(p => p.cardId === x.id && !p.hidden));

  return (
    <div className="space-y-4">
      {!consent && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <ShieldAlert className="h-4 w-4" />
          <span className="min-w-0 flex-1">Khách <b>chưa ký đồng ý sử dụng hình ảnh</b>. Nên cho khách ký trước khi chụp để lưu ảnh vào hồ sơ.</span>
          <Btn onClick={() => ops.sign({ kind: 'photo' })}>Ký đồng ý hình ảnh</Btn>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select className="min-w-[200px]" value={cardFilter} onChange={setCardFilter}
          options={[{ value: 'all', label: 'Tất cả thẻ' }, ...c.data.cards.map(x => ({ value: x.id, label: `${x.packageName} · ${x.id}` })), { value: 'none', label: 'Không gắn thẻ' }]} />
        <Segmented value={phase} onChange={v => setPhase(v as typeof phase)} options={[{ value: 'all', label: 'Tất cả' }, { value: 'before', label: 'Trước' }, { value: 'during', label: 'Trong' }, { value: 'after', label: 'Sau' }]} />
        <Checkbox checked={showHidden} onChange={e => setShowHidden(e.target.checked)}><span className="text-xs">Hiện ảnh đã ẩn</span></Checkbox>
        <div className="ml-auto flex flex-wrap gap-2">
          {cardsWithPhotos.length > 0 && (
            <Select size="middle" placeholder="So sánh nhanh theo thẻ" value={null as unknown as string} onChange={quickCompare} className="min-w-[190px]"
              options={cardsWithPhotos.map(x => ({ value: x.id, label: `Trước/sau · ${x.packageName}` }))} />
          )}
          <Btn onClick={() => { setSelecting(s => !s); setPicked([]); }}><Columns2 className="h-3.5 w-3.5" /> {selecting ? 'Thôi chọn' : 'Chọn 2 ảnh so sánh'}</Btn>
          {selecting && picked.length === 2 && <Btn primary onClick={() => { const [a, b] = picked.map(id => flat.find(p => p.id === id)!).sort((x, y) => x.takenAt.localeCompare(y.takenAt)); setCompare([a, b]); }}>So sánh</Btn>}
          {can('create') && <Btn primary onClick={() => setUpload({})}><ImagePlus className="h-4 w-4" /> Thêm ảnh</Btn>}
        </div>
      </div>

      {!groups.length && <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400">Chưa có ảnh liệu trình</div>}

      {groups.map(g => (
        <div key={g.key}>
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
            {g.label} <span className="text-xs font-normal text-slate-400">({g.items.length} ảnh)</span>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {g.items.map(p => (
              <Thumb key={p.id} p={p} selected={picked.includes(p.id)} selecting={selecting}
                onClick={() => (selecting ? togglePick(p.id) : setViewing(p.id))} />
            ))}
          </div>
        </div>
      ))}

      <UploadModal open={!!upload} preset={upload} c={c} consent={consent} onClose={() => setUpload(null)} />
      <Viewer id={viewing} list={flat} c={c} onClose={() => setViewing(null)} onNav={setViewing} />
      <CompareModal pair={compare} onClose={() => setCompare(null)} />
    </div>
  );
}

function Thumb({ p, selected, selecting, onClick }: { p: TreatmentPhoto; selected: boolean; selecting: boolean; onClick: () => void }) {
  const src = useMedia(p.thumbId);
  return (
    <button type="button" onClick={onClick}
      className={`group relative aspect-square overflow-hidden rounded-lg border-2 bg-slate-100 ${selected ? 'border-[#3e91ab] ring-2 ring-[#3e91ab]/40' : 'border-transparent'} ${p.hidden ? 'opacity-50' : ''}`}>
      {src ? <img src={src} alt={p.angle} className="h-full w-full object-cover transition group-hover:scale-105" /> : <Spin size="small" className="mt-[40%]" />}
      <span className={`absolute left-1 top-1 rounded border px-1.5 text-[10px] font-semibold ${PHASE_CLS[p.phase]}`}>{PHASE_LABEL[p.phase]}</span>
      {selecting && <span className={`absolute right-1 top-1 h-4 w-4 rounded-full border-2 border-white ${selected ? 'bg-[#3e91ab]' : 'bg-black/30'}`} />}
      <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/60 to-transparent px-1.5 pb-1 pt-3 text-left text-[10px] text-white">{p.angle}{p.hidden ? ' · đã ẩn' : ''}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------

interface Pending { file: File; preview: string; angle: string }

function UploadModal({ open, preset, c, consent, onClose }: { open: boolean; preset: { cardId?: string; usageId?: string } | null; c: C360; consent: boolean; onClose: () => void }) {
  const addPhotos = useC360Store(s => s.addPhotos);
  const { userName } = useTreatmentAuth();
  const [files, setFiles] = useState<Pending[]>([]);
  const [cardId, setCardId] = useState<string>('');
  const [usageId, setUsageId] = useState<string>('');
  const [phase, setPhase] = useState<PhotoPhase>('before');
  const [takenAt, setTakenAt] = useState(dayjs());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setCardId(preset?.cardId || '');
    setUsageId(preset?.usageId || '');
    const card = c.data.cards.find(x => x.id === preset?.cardId);
    const u = card?.usages.find(x => x.id === preset?.usageId);
    setTakenAt(u ? dayjs(u.at) : dayjs());
    setPhase(!card || !card.usages.some(x => !x.cancelled) ? 'before' : 'after');
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const card = c.data.cards.find(x => x.id === cardId);
  const usages = (card?.usages || []).filter(u => !u.cancelled).sort((a, b) => a.at.localeCompare(b.at));

  const pick = (list: FileList | null) => {
    if (!list) return;
    const next = Array.from(list).filter(f => f.type.startsWith('image/')).slice(0, 12)
      .map((file, i) => ({ file, preview: URL.createObjectURL(file), angle: PHOTO_ANGLES[(files.length + i) % 3] }));
    setFiles(f => [...f, ...next].slice(0, 12));
  };

  const save = async () => {
    if (!files.length) return;
    setSaving(true);
    try {
      const items = [];
      for (const f of files) {
        const full = await compressImage(f.file, 1600, 0.82);
        const thumb = await compressImage(f.file, 360, 0.7);
        items.push({
          customerKey: c.key, cardId: cardId || undefined, usageId: usageId || undefined, sessionNo: sessionNoOf(card, usageId || undefined),
          phase, angle: f.angle, note, takenAt: takenAt.toISOString(), by: userName, dataUrl: full.dataUrl, thumbUrl: thumb.dataUrl,
        });
      }
      await addPhotos(items);
      message.success(`Đã lưu ${items.length} ảnh`);
      files.forEach(f => URL.revokeObjectURL(f.preview));
      onClose();
    } catch {
      message.error('Không lưu được ảnh – bộ nhớ trình duyệt đầy hoặc bị chặn');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} title="Thêm ảnh liệu trình" centered width={720} destroyOnHidden okText={saving ? 'Đang lưu…' : `Lưu ${files.length || ''} ảnh`} cancelText="Hủy"
      onOk={save} okButtonProps={{ disabled: !files.length || saving }}>
      <div className="space-y-3 text-sm">
        {!consent && <div className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-800">Khách chưa ký đồng ý sử dụng hình ảnh – ảnh vẫn lưu nhưng sẽ hiện nhắc ký.</div>}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block"><span className="mb-1 block font-medium">Thẻ liệu trình</span>
            <Select className="w-full" value={cardId} onChange={v => { setCardId(v); setUsageId(''); }}
              options={[{ value: '', label: 'Không gắn thẻ' }, ...c.data.cards.map(x => ({ value: x.id, label: `${x.packageName} · ${x.id}` }))]} /></label>
          <label className="block"><span className="mb-1 block font-medium">Buổi</span>
            <Select className="w-full" value={usageId} onChange={v => { setUsageId(v); const u = usages.find(x => x.id === v); if (u) setTakenAt(dayjs(u.at)); }} disabled={!card}
              options={[{ value: '', label: card ? 'Chưa bắt đầu / tư vấn' : '—' }, ...usages.map((u, i) => ({ value: u.id, label: `Buổi ${i + 1} · ${dayjs(u.at).format('DD/MM/YYYY')} · ${u.items.map(x => x.name).join(', ')}` }))]} /></label>
          <label className="block"><span className="mb-1 block font-medium">Giai đoạn</span>
            <Segmented block value={phase} onChange={v => setPhase(v as PhotoPhase)} options={[{ value: 'before', label: 'Trước' }, { value: 'during', label: 'Trong' }, { value: 'after', label: 'Sau' }]} /></label>
          <label className="block"><span className="mb-1 block font-medium">Thời điểm chụp</span>
            <DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" value={takenAt} onChange={d => d && setTakenAt(d)} allowClear={false} /></label>
        </div>
        <Input.TextArea rows={2} maxLength={300} value={note} onChange={e => setNote(e.target.value)} placeholder="Ghi chú tình trạng (VD: mụn viêm vùng má trái, đỏ nhẹ sau peel)" />

        <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 py-6 text-slate-500 hover:border-[#3e91ab]"
          onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); pick(e.dataTransfer.files); }}>
          <Upload className="h-6 w-6" />
          <span className="font-medium">Chụp ảnh / chọn ảnh / kéo thả vào đây</span>
          <span className="text-xs">Tối đa 12 ảnh mỗi lần · ảnh được nén còn ~1600px</span>
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => { pick(e.target.files); e.target.value = ''; }} />
        </label>

        {files.length > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {files.map((f, i) => (
              <div key={f.preview} className="relative overflow-hidden rounded-lg border border-slate-200">
                <img src={f.preview} alt="" className="aspect-square w-full object-cover" />
                <button onClick={() => setFiles(x => x.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded bg-black/50 p-1 text-white"><Trash2 className="h-3.5 w-3.5" /></button>
                <Select size="small" className="w-full" value={f.angle} onChange={v => setFiles(x => x.map((y, j) => (j === i ? { ...y, angle: v } : y)))}
                  options={PHOTO_ANGLES.map(a => ({ value: a, label: a }))} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function Viewer({ id, list, c, onClose, onNav }: { id: string | null; list: TreatmentPhoto[]; c: C360; onClose: () => void; onNav: (id: string) => void }) {
  const p = list.find(x => x.id === id) || c.data.photos.find(x => x.id === id);
  const src = useMedia(p?.mediaId);
  const updatePhoto = useC360Store(s => s.updatePhoto);
  const hidePhoto = useC360Store(s => s.hidePhoto);
  const restorePhoto = useC360Store(s => s.restorePhoto);
  const removePhoto = useC360Store(s => s.removePhoto);
  const { can, userName } = useTreatmentAuth();
  const hideReason = useRef('');
  const idx = list.findIndex(x => x.id === id);
  if (!p) return null;
  const card = c.data.cards.find(x => x.id === p.cardId);

  const hide = () => Modal.confirm({
    title: 'Ẩn ảnh khỏi hồ sơ?', okText: 'Ẩn ảnh', cancelText: 'Không',
    content: <Input placeholder="Lý do (VD: khách yêu cầu, ảnh mờ)" onChange={e => { hideReason.current = e.target.value; }} />,
    onOk: () => { hidePhoto(p.id, userName, hideReason.current.trim() || 'Không ghi lý do'); hideReason.current = ''; },
  });
  const remove = () => Modal.confirm({
    title: 'Xóa hẳn ảnh này?', content: 'Ảnh bị xóa khỏi thiết bị, không khôi phục được. Chỉ dùng cho ảnh chụp nhầm khách.', okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Không',
    onOk: async () => { await removePhoto(p.id); onClose(); },
  });

  return (
    <Modal open={!!id} onCancel={onClose} footer={null} centered width={980} title={`${PHASE_LABEL[p.phase]} · ${p.angle}`} destroyOnHidden>
      <div className="grid gap-4 md:grid-cols-[1fr_260px]">
        <div className="relative flex min-h-[320px] items-center justify-center rounded-lg bg-slate-900">
          {src ? <img src={src} alt="" className="max-h-[70vh] max-w-full object-contain" /> : <Spin />}
          {idx > 0 && <button onClick={() => onNav(list[idx - 1].id)} className="absolute left-2 rounded-full bg-white/80 p-1.5"><ChevronLeft className="h-5 w-5" /></button>}
          {idx >= 0 && idx < list.length - 1 && <button onClick={() => onNav(list[idx + 1].id)} className="absolute right-2 rounded-full bg-white/80 p-1.5"><ChevronRight className="h-5 w-5" /></button>}
        </div>
        <div className="space-y-2 text-sm">
          <div className="text-xs text-slate-500">{p.id} · chụp {fmtDateTime(p.takenAt)} · lưu bởi {p.by}</div>
          <div><span className="text-slate-500">Thẻ:</span> {card ? `${card.packageName}${p.sessionNo ? ` · buổi ${p.sessionNo}` : ''}` : 'Không gắn thẻ'}</div>
          {can('edit') ? <>
            <Segmented size="small" block value={p.phase} onChange={v => updatePhoto(p.id, { phase: v as PhotoPhase })} options={(Object.keys(PHASE_LABEL) as PhotoPhase[]).map(k => ({ value: k, label: PHASE_LABEL[k] }))} />
            <Select size="small" className="w-full" value={p.angle} onChange={v => updatePhoto(p.id, { angle: v })} options={PHOTO_ANGLES.map(a => ({ value: a, label: a }))} />
            <Input.TextArea rows={3} defaultValue={p.note} key={p.id} onBlur={e => e.target.value !== p.note && updatePhoto(p.id, { note: e.target.value })} placeholder="Ghi chú" />
          </> : p.note && <div className="rounded bg-slate-50 p-2">{p.note}</div>}
          {p.hidden && <div className="rounded bg-slate-100 p-2 text-xs text-slate-600">Đã ẩn bởi {p.hidden.by} · {fmtDateTime(p.hidden.at)}: {p.hidden.reason}</div>}
          <div className="flex flex-wrap gap-2 pt-1">
            {can('edit') && (p.hidden ? <Btn onClick={() => restorePhoto(p.id)}>Hiện lại</Btn> : <Btn onClick={hide}><EyeOff className="h-3.5 w-3.5" /> Ẩn ảnh</Btn>)}
            {can('delete') && <Btn danger onClick={remove}><Trash2 className="h-3.5 w-3.5" /> Xóa</Btn>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function CompareModal({ pair, onClose }: { pair: [TreatmentPhoto, TreatmentPhoto] | null; onClose: () => void }) {
  const [mode, setMode] = useState<'side' | 'slider'>('side');
  const [pos, setPos] = useState(50);
  const a = useMedia(pair?.[0].mediaId);
  const b = useMedia(pair?.[1].mediaId);
  if (!pair) return null;
  const caption = (p: TreatmentPhoto) => `${PHASE_LABEL[p.phase]} · ${dayjs(p.takenAt).format('DD/MM/YYYY')}${p.sessionNo ? ` · buổi ${p.sessionNo}` : ''} · ${p.angle}`;
  const days = dayjs(pair[1].takenAt).diff(dayjs(pair[0].takenAt), 'day');
  return (
    <Modal open onCancel={onClose} footer={null} centered width={1040} destroyOnHidden
      title={<span className="flex flex-wrap items-center gap-3">So sánh kết quả <span className="text-xs font-normal text-slate-500">cách nhau {days} ngày</span>
        <Segmented size="small" value={mode} onChange={v => setMode(v as typeof mode)} options={[{ value: 'side', label: <span className="flex items-center gap-1"><Columns2 className="h-3.5 w-3.5" /> Cạnh nhau</span> }, { value: 'slider', label: <span className="flex items-center gap-1"><SlidersHorizontal className="h-3.5 w-3.5" /> Thanh trượt</span> }]} /></span>}>
      {mode === 'side' ? (
        <div className="grid grid-cols-2 gap-3">
          {[[pair[0], a], [pair[1], b]].map(([p, src], i) => (
            <figure key={i} className="m-0">
              <div className="flex aspect-[3/4] items-center justify-center overflow-hidden rounded-lg bg-slate-900">{src ? <img src={src as string} alt="" className="h-full w-full object-contain" /> : <Spin />}</div>
              <figcaption className="mt-1 text-center text-sm font-medium text-slate-700">{caption(p as TreatmentPhoto)}</figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <div>
          <div className="relative mx-auto aspect-[3/4] max-h-[70vh] select-none overflow-hidden rounded-lg bg-slate-900">
            {b && <img src={b} alt="" className="absolute inset-0 h-full w-full object-contain" />}
            {a && <img src={a} alt="" className="absolute inset-0 h-full w-full object-contain" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />}
            <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `${pos}%` }} />
            <span className="absolute left-2 top-2 rounded bg-black/60 px-2 text-xs text-white">{PHASE_LABEL[pair[0].phase]}</span>
            <span className="absolute right-2 top-2 rounded bg-black/60 px-2 text-xs text-white">{PHASE_LABEL[pair[1].phase]}</span>
          </div>
          <input type="range" min={0} max={100} value={pos} onChange={e => setPos(Number(e.target.value))} className="mt-3 w-full accent-[#3e91ab]" />
          <div className="flex justify-between text-xs text-slate-500"><span>{caption(pair[0])}</span><span>{caption(pair[1])}</span></div>
        </div>
      )}
    </Modal>
  );
}
